/**
 * <CloseShiftDrawer> — 3-gate close per D17 + PR2-1 denomination counting.
 * Gates:
 *   a) cash counted (via DenominationCounter + optional manual override)
 *   b) pending issues acknowledged
 *   c) supervisor / teammate acknowledgement
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  OperationalDrawer, BottomActionBar, OperationalCard, StatusChip,
} from "@/components/operational";
import { DenominationCounter } from "@/components/operational/DenominationCounter";
import { LogOut } from "lucide-react";
import { foApi, money, formatApiError } from "../foApi";
import { NumberInput } from "@/components/ui/number-input";

// Denomination major-unit values (mirrors backend)
const NOTE_VALUES = {
  "2000": 200000, "500": 50000, "200": 20000, "100": 10000,
  "50": 5000, "20": 2000, "10": 1000, "5": 500, "2": 200, "1": 100,
};

function sumBreakdownMinor(breakdown) {
  let t = 0;
  for (const [k, n] of Object.entries(breakdown || {})) {
    if (k === "coins") t += Number(n) || 0;
    else t += (Number(n) || 0) * (NOTE_VALUES[k] || 0);
  }
  return t;
}

export default function CloseShiftDrawer({ open, onOpenChange, shift, kpis, onSuccess }) {
  const [breakdown, setBreakdown] = useState({});
  const [ack, setAck] = useState(false);
  const [teammates, setTeammates] = useState([]);
  const [supervisor, setSupervisor] = useState("");
  const [notes, setNotes] = useState("");
  const [override, setOverride] = useState(false);
  const [manualCounted, setManualCounted] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setBreakdown({}); setAck(false); setSupervisor(""); setNotes("");
    setOverride(false); setManualCounted("");
    foApi.shiftTeammates({ hotel_id: shift?.hotel_id })
      .then(r => setTeammates(r.items || [])).catch(() => setTeammates([]));
  }, [open, shift?.hotel_id]);

  const breakdownTotalMinor = useMemo(() => sumBreakdownMinor(breakdown), [breakdown]);
  const countedMinor = override
    ? Math.round((parseFloat(manualCounted || "0") || 0) * 100)
    : breakdownTotalMinor;

  const expected = kpis?.expected_cash_minor || 0;
  const variance = countedMinor - expected;

  const hasCounted = override ? manualCounted !== "" : countedMinor > 0 || Object.keys(breakdown).length > 0;
  const canSubmit = hasCounted && ack && Boolean(supervisor);

  const submit = async () => {
    if (!canSubmit) { toast.error("All 3 gates required"); return; }
    setSaving(true);
    try {
      await foApi.closeShift(shift.id, {
        closing_counted_minor: countedMinor,
        handover_ack_by: supervisor,
        pending_issues_ack: ack,
        notes: notes || null,
        denomination_breakdown: override ? null : (breakdown && Object.keys(breakdown).length ? breakdown : null),
        manual_override: override,
      });
      onSuccess?.();
    } catch (e) {
      toast.error(formatApiError(e, "Failed to close shift"));
    } finally { setSaving(false); }
  };

  return (
    <OperationalDrawer open={open} onOpenChange={onOpenChange}
      title="Close Shift" subtitle="Complete all 3 gates to close"
      size="md" testid="close-shift-drawer"
      footer={
        <BottomActionBar
          primary={{ label: saving ? "Closing..." : "Close Shift",
                     icon: LogOut, tone: "danger", onClick: submit,
                     loading: saving, disabled: !canSubmit,
                     testid: "close-shift-confirm" }}
          secondaryActions={[
            { label: "Cancel", tone: "secondary", onClick: () => onOpenChange(false), testid: "close-shift-cancel" }
          ]}
          helper={canSubmit ? "All gates passed" : "Complete every gate below"}
        />
      }>
      <div className="p-4 space-y-4">
        {/* Expected vs counted */}
        <OperationalCard tone="info">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Opening float</div>
              <div className="text-[16px] font-bold">{money(kpis?.opening_float_minor || 0)}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Expected cash</div>
              <div className="text-[16px] font-bold text-sky-800">{money(expected)}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Variance</div>
              <div className={`text-[16px] font-bold ${!hasCounted ? "text-slate-400" : (variance === 0 ? "text-emerald-700" : "text-amber-700")}`} data-testid="close-shift-variance">
                {!hasCounted ? "—" : money(variance)}
              </div>
            </div>
          </div>
        </OperationalCard>

        {/* Gate A: cash counted via denominations */}
        <div>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-[12px] font-bold text-slate-500">GATE A</span>
            <span className="text-[13px] font-semibold">Physical cash counted</span>
            <StatusChip status={hasCounted ? "ready" : "pending"} label={hasCounted ? "Done" : "Required"} size="sm"/>
            <div className="ml-auto flex items-center gap-2">
              <label className="text-[12px] text-slate-600 flex items-center gap-1 cursor-pointer" data-testid="close-shift-override-label">
                <input type="checkbox" checked={override}
                       onChange={e => setOverride(e.target.checked)}
                       data-testid="close-shift-override"/>
                Manual override
              </label>
            </div>
          </div>

          {!override ? (
            <>
              <DenominationCounter value={breakdown} onChange={setBreakdown} testid="close-shift-denoms"/>
              <div className="mt-2 flex items-center justify-between text-[13px]">
                <span className="text-slate-500">Total from breakdown</span>
                <span className="font-bold" data-testid="close-shift-breakdown-total">
                  {money(breakdownTotalMinor)}
                </span>
              </div>
            </>
          ) : (
            <>
              <NumberInput min={0} step="0.01" value={manualCounted}
                     onChange={e => setManualCounted(e.target.value)}
                     className="w-full mt-1 border-2 border-slate-300 rounded-lg p-3 text-[18px] font-semibold h-14"
                     placeholder="0.00 (₹)" aria-label="Counted cash amount (₹)"
                     data-testid="close-shift-counted-manual"/>
              <div className="mt-1 text-[12px] text-amber-700">
                Manual override — an authorised supervisor must be selected below.
              </div>
            </>
          )}

          {hasCounted && Math.abs(variance) > 10000 && (
            <div className="mt-2 text-[12px] text-amber-700" data-testid="close-shift-tolerance-warning">
              Variance exceeds ₹100 tolerance — supervisor approval will be requested.
            </div>
          )}
        </div>

        {/* Gate B: pending issues ack */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[12px] font-bold text-slate-500">GATE B</span>
            <span className="text-[13px] font-semibold">Pending issues acknowledged</span>
            <StatusChip status={ack ? "ready" : "pending"} label={ack ? "Done" : "Required"} size="sm"/>
          </div>
          <button
            onClick={() => setAck(v => !v)}
            className={`w-full min-h-[48px] px-4 rounded-lg border-2 text-[14px] font-medium text-left ${ack ? "bg-emerald-50 border-emerald-500 text-emerald-800" : "bg-white border-slate-300"}`}
            data-testid="close-shift-ack">
            {ack ? "I have logged all pending issues (VIP, callbacks, complaints)"
                 : "Click to confirm: I have logged all pending issues"}
          </button>
        </div>

        {/* Gate C: handover teammate */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[12px] font-bold text-slate-500">GATE C</span>
            <span className="text-[13px] font-semibold">Handover acknowledgement</span>
            <StatusChip status={supervisor ? "ready" : "pending"} label={supervisor ? "Done" : "Required"} size="sm"/>
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="close-shift-supervisor-list">
            {teammates.length === 0 ? (
              <div className="text-[12px] text-slate-500">No teammates found — enter a name / ID manually below.</div>
            ) : teammates.slice(0, 10).map(t => (
              <button key={t.id} onClick={() => setSupervisor(t.id)}
                className={`min-h-[44px] px-3 rounded-lg border-2 text-[13px] font-medium ${supervisor === t.id ? "bg-hocp-mint text-hocp-deepTeal border-hocp-mint" : "bg-white border-slate-300"}`}
                data-testid={`close-shift-supervisor-${t.id}`}>
                {t.name}
              </button>
            ))}
          </div>
          {teammates.length === 0 && (
            <input type="text" value={supervisor} onChange={e => setSupervisor(e.target.value)}
                   className="w-full mt-2 border-2 border-slate-300 rounded-lg p-3 text-[14px] h-12"
                   placeholder="Handover received by (name / ID)" aria-label="Handover received by (name / ID)"
                   data-testid="close-shift-supervisor-manual"/>
          )}
        </div>

        {/* Handover notes */}
        <div>
          <label className="text-[12px] uppercase tracking-wider text-slate-500 font-semibold">Handover notes</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)}
                    className="w-full mt-1 border-2 border-slate-300 rounded-lg p-2 text-[14px] min-h-[100px]"
                    placeholder="Anything the incoming shift needs to know…"
                    data-testid="close-shift-notes"/>
        </div>
      </div>
    </OperationalDrawer>
  );
}
